/**
 * AcidRainSkillEffect — Poison tree corrode-path active: lobbed toxic payload.
 *
 * Fires a lobbed orb projectile (`type: 'orb'`, `impactDamage: 0`) toward the
 * targeted position. On landing, the projectile creates a circular `acid_rain_pool`
 * zone (2-tile radius, 10 seconds) that applies amplified `poison` DoT at 1000ms
 * intervals. The pool is the weapon — the impact itself deals no damage.
 *
 * Footprint trail: when any enemy exits the pool, `onEntityExit` spawns a
 * `'toxic_footprint'` zone (0.5-tile radius, 3 seconds, oneShot per entity)
 * at the enemy's exit position. Enemies walking through footprints receive one
 * DoT application and are then immune to that footprint.
 *
 * Slick (acid_rain_upgrade_1): on pool expiry, `onExpiry` spawns a
 * `'contact_coating'` zone at the pool's position (same radius, 4 seconds, oneShot
 * per entity). Contact coatings deliver one DoT per entity on first crossing.
 *
 * Saturation (acid_rain_upgrade_2): pool DoT stacking switches from the global
 * `refresh` rule to `independent` with up to 5 concurrent instances per target.
 * This stacking override is carried via the ZoneEvent's `dotStackingRule` field
 * when Saturation is unlocked, and applied by CombatHitResolver on processing.
 *
 * Intensity inherits Potent Compound (scope: 'taint') and Toxicologist multiplier,
 * exactly matching Haze's amplified baseline (5/tick).
 *
 * Cooldown tier: long (2000ms).
 *
 * The game engine calls `notifyImpact(x, y)` when the projectile lands. The sim
 * harness calls this directly to test zone creation without physics.
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Acid Rain, Slick, Saturation
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ZoneConfig, ActiveZone } from '../../../systems/ZoneSystem.js';
import type { PlayerProjectileConfig } from '../../../systems/PlayerProjectilePool.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AcidRainDeps {
  readonly zoneSystem: {
    create(config: ZoneConfig): string;
    getZone(id: string): ActiveZone | undefined;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly targeting: {
    /** Returns the world-space position the player is targeting. */
    getTargetPosition(): { x: number; y: number };
  };
  readonly projectilePool: {
    fire(
      config: PlayerProjectileConfig,
      fromX: number,
      fromY: number,
      dirX: number,
      dirY: number,
    ): number;
  };
  readonly playerPosition: {
    getPosition(): { x: number; y: number };
  };
  /** Required for Choking: apply post-exposure slow when entity exits after ≥ threshold. */
  readonly applySlowEffect?: (
    entityId: string,
    opts: { sourceSkillId: string; durationMs: number; intensity: number },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AcidRainConfig {
  readonly cooldownMs: number;               // long tier = 2000ms
  readonly baseRadiusTiles: number;          // 2 tiles
  readonly baseDurationMs: number;           // 10000ms
  readonly baseIntensity: number;            // 5 (amplified, same as Haze)
  readonly footprintRadiusTiles: number;     // 0.5 tiles
  readonly footprintDurationMs: number;      // 3000ms
  readonly footprintTickIntervalMs: number;  // 250ms — quick first tick for oneShot
  readonly projectileSpeedPxPerSec: number;  // 200
  readonly projectileRangeTiles: number;     // 8 — enough to reach any room target
  readonly chokingThresholdMs: number;       // 3000ms — minimum exposure for post-exit slow
}

const DEFAULT_CONFIG: AcidRainConfig = {
  cooldownMs: 2000,
  baseRadiusTiles: 2,
  baseDurationMs: 10000,
  baseIntensity: 5,
  footprintRadiusTiles: 0.5,
  footprintDurationMs: 3000,
  footprintTickIntervalMs: 250,
  projectileSpeedPxPerSec: 200,
  projectileRangeTiles: 8,
  chokingThresholdMs: 3000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AcidRainSkillEffect implements SkillEffect {
  readonly skillId = 'acid_rain';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: AcidRainDeps;
  private readonly config: AcidRainConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  private _poolZoneIds: string[] = [];
  private _footprintCount = 0;
  private _lastProjectileId = -1;

  constructor(deps: AcidRainDeps, config: Partial<AcidRainConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get poolZoneIds(): readonly string[] { return this._poolZoneIds; }
  get lastProjectileId(): number { return this._lastProjectileId; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const playerPos = this.deps.playerPosition.getPosition();
    const targetPos = this.deps.targeting.getTargetPosition();

    const dx = targetPos.x - playerPos.x;
    const dy = targetPos.y - playerPos.y;

    this._lastProjectileId = this.deps.projectilePool.fire(
      {
        type: 'orb',
        damage: 0,
        speed: this.config.projectileSpeedPxPerSec,
        rangeTiles: this.config.projectileRangeTiles,
        sourceSkillId: 'acid_rain',
        impactRadiusTiles: this.config.baseRadiusTiles,
        impactDamage: 0,
      },
      playerPos.x,
      playerPos.y,
      dx,
      dy,
    );

    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;
    return true;
  }

  /**
   * Called by the game engine (or sim harness) when the Acid Rain orb lands
   * at world position (x, y). Creates the `acid_rain_pool` zone with all
   * upgrade hooks (footprint exit callback, Slick expiry callback).
   *
   * Returns the zone ID of the created pool.
   */
  notifyImpact(x: number, y: number): string {
    const intensity = this.resolveIntensity();
    const poolIndex = this._activationsCount - 1;
    const poolId = `acid_rain_pool_${poolIndex}`;

    // Slick (acid_rain_upgrade_1): spawn contact_coating on pool expiry
    const coatingDurationMs = this.deps.resolver.getAdditiveEffect(
      'acid_rain_upgrade_1',
      'contactCoatingDurationMs',
    );
    const onExpiry = coatingDurationMs > 0
      ? this._makeSlickExpiryCallback(x, y, coatingDurationMs, intensity, poolIndex)
      : undefined;

    // Saturation (acid_rain_upgrade_2): switch pool DoT from refresh to
    // independent stacking with up to 5 concurrent instances per target.
    const maxDotStacks = this.deps.resolver.getAdditiveEffect(
      'acid_rain_upgrade_2',
      'maxDotStacks',
    );
    const dotStackingRule = maxDotStacks > 0 ? 'independent' : undefined;
    const dotMaxStacks = maxDotStacks > 0 ? maxDotStacks : undefined;

    // Compose onEntityExit: footprint trail + Choking post-exposure slow
    const footprintCallback = this._makeFootprintCallback(intensity);
    const chokingCallback = this._makeChokingExitCallback();
    const onEntityExit = chokingCallback
      ? (entityId: string, zone: ActiveZone, dwellMs: number, exitX: number, exitY: number) => {
          footprintCallback(entityId, zone, dwellMs, exitX, exitY);
          chokingCallback(entityId, zone, dwellMs, exitX, exitY);
        }
      : footprintCallback;

    this.deps.zoneSystem.create({
      id: poolId,
      type: 'acid_rain_pool',
      position: { x, y },
      durationMs: this.config.baseDurationMs,
      sourceSkillId: 'acid_rain',
      shape: 'circle',
      radiusTiles: this.config.baseRadiusTiles,
      dotDefId: 'poison',
      dotIntensity: intensity,
      tickIntervalMs: 1000,
      dotStackingRule,
      dotMaxStacks,
      onEntityExit,
      onExpiry,
    });

    this._poolZoneIds.push(poolId);
    return poolId;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Footprint trail: on any enemy exit from the pool, spawn a `'toxic_footprint'`
   * zone at the enemy's exit position. Each footprint is oneShot per entity —
   * an enemy triggers it once, then is marked used.
   *
   * The exitX/exitY are provided by ZoneSystem from the entity's current frame
   * position (the position they moved to when they left the zone).
   */
  private _makeFootprintCallback(
    intensity: number,
  ): (entityId: string, zone: ActiveZone, dwellMs: number, exitX: number, exitY: number) => void {
    return (_entityId: string, _zone: ActiveZone, _dwellMs: number, exitX: number, exitY: number) => {
      const footprintId = `toxic_footprint_${this._footprintCount++}`;
      this.deps.zoneSystem.create({
        id: footprintId,
        type: 'toxic_footprint',
        position: { x: exitX, y: exitY },
        durationMs: this.config.footprintDurationMs,
        sourceSkillId: 'acid_rain',
        shape: 'circle',
        radiusTiles: this.config.footprintRadiusTiles,
        dotDefId: 'poison',
        dotIntensity: intensity,
        tickIntervalMs: this.config.footprintTickIntervalMs,
        oneShot: true,
      });
    };
  }

  /**
   * Slick: on pool expiry, spawn a `'contact_coating'` zone at the pool's
   * original footprint. The coating is oneShot per entity — first contact
   * delivers one DoT, then the entity is immune to that coating.
   */
  private _makeSlickExpiryCallback(
    poolX: number,
    poolY: number,
    coatingDurationMs: number,
    intensity: number,
    poolIndex: number,
  ): (zone: ActiveZone) => void {
    const coatingId = `contact_coating_${poolIndex}`;
    const radiusTiles = this.config.baseRadiusTiles;

    return (_zone: ActiveZone) => {
      this.deps.zoneSystem.create({
        id: coatingId,
        type: 'contact_coating',
        position: { x: poolX, y: poolY },
        durationMs: coatingDurationMs,
        sourceSkillId: 'acid_rain',
        shape: 'circle',
        radiusTiles,
        dotDefId: 'poison',
        dotIntensity: intensity,
        tickIntervalMs: 250,
        oneShot: true,
      });
    };
  }

  /**
   * Choking: build the onEntityExit callback for post-exposure slow.
   * Returns undefined if Choking is not unlocked (postExposureSlowAmount == 0)
   * or applySlowEffect is not injected.
   */
  private _makeChokingExitCallback():
    | ((entityId: string, _zone: ActiveZone, dwellMs: number, _exitX: number, _exitY: number) => void)
    | undefined {
    const slowAmount = this.deps.resolver.getAdditiveEffect('acid_rain', 'postExposureSlowAmount');
    if (slowAmount <= 0 || !this.deps.applySlowEffect) return undefined;

    const slowDurationMs = this.deps.resolver.getAdditiveEffect('acid_rain', 'postExposureSlowDurationMs');
    const thresholdMs = this.config.chokingThresholdMs;
    const applySlowEffect = this.deps.applySlowEffect;

    return (entityId: string, _zone, dwellMs: number) => {
      if (dwellMs >= thresholdMs) {
        applySlowEffect(entityId, {
          sourceSkillId: 'acid_rain',
          durationMs: slowDurationMs,
          intensity: slowAmount,
        });
      }
    };
  }

  private resolveIntensity(): number {
    const base = this.config.baseIntensity;
    // Inherit Potent Compound from the shared trunk (scope: 'taint')
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDamagePerTick');
    const multiplier = this.deps.resolver.getTreePassiveEffect(
      'poison', 'dotDamageMultiplier', 'product',
    );
    return (base + bonus) * multiplier;
  }
}
